import React, { useCallback } from 'react';
import { EDDialogCard } from '../../BaseComponents/Card/DialogCard/EDDialogCard';
import { makeStyles } from 'tss-react/mui';
import { IColumnDef } from '../../Types/ColumnDefType';
import CheckboxSelectListItemView from '../KolumnValjare/Components/CheckboxSelectListItem/CheckboxSelectListItemView';
import DraggableChipList from '../../BaseComponents/Chip/DraggableChip/EDDraggableChipList';
import EDGrid from '../../BaseComponents/GridComponents/EDGrid';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import EDButton from '../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../BaseComponents/Icon/EDIcon';
import { ISortProperty } from '../../BaseComponents/Table/Components/SortableTableUtils';
import { clone } from 'ramda';
import EDViewportWidthContainer from '../../BaseComponents/ViewportWidthContainer/EDViewportWidthContainer';
import CheckboxSelectList from '../KolumnValjare/Components/CheckboxSelectList/CheckboxSelectList';
import { EDListItemButton } from '../../BaseComponents/List/Components/EDListItem';
import EDIconButton from '../../BaseComponents/Buttons/IconButton/EDIconButton';

interface IProps {
  kolumner: readonly IColumnDef[];
  sortProperties: ISortProperty[];
  updateSortProperties: (proprties: ISortProperty[]) => void;
}

const useStyles = makeStyles()(
  theme =>
    ({
      mainContainer: {
        flex: 1,
        overflow: 'hidden',
      },
      flexContainer: {
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      },
      container: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      listItem: {
        alignItems: 'flex-start',
        flexDirection: 'column',
        padding: '4px 0px',
        '&:hover': {
          backgroundColor: 'inherit',
        },
      },
      titleText: {
        fontWeight: 700,
      },
      searchText: {
        background: 'inherit',
        padding: '8px 8px 8px 13px',
        'label + &': {
          margin: 0,
        },
      },
      searchInput: {
        paddingTop: 0,
        paddingBottom: 0,
      },
      iconMargin: {
        marginRight: 8,
      },
      titleHeight: {
        height: 40,
      },
    } as const)
);

/**
 * En komponent för att välja sortering. En lista av möjliga sorteringsbegrepp ligger i högersidan som kan läggas till som valda. Valda begrepp kan sedan sättas som stigande eller fallande ordning.
 */
const SorteringsValjare: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { updateSortProperties } = props;
  const { classes, cx } = useStyles();
  return (
    <EDViewportWidthContainer classes={{ container: classes.flexContainer }}>
      <EDGrid container className={classes.mainContainer}>
        <EDGrid item xs={12} sm={6} className={classes.flexContainer}>
          <div className={classes.container}>
            <EDTypography className={classes.titleText} gutterBottom>
              Sorteringsinställningar
            </EDTypography>
            <EDButton
              label="Rensa alla"
              icon={<EDIcon color="primary" iconType="clear_all" />}
              onClick={useCallback(
                (event: React.MouseEvent) => updateSortProperties([]),
                [updateSortProperties]
              )}
              flat
              primary
              padding={1}
              labelNotUpperCase
              lowEmphasis
            />
          </div>
          <EDDialogCard displayFlex>
            <DraggableChipList
              items={props.sortProperties.map((sortering, index) => ({
                id: sortering.orderBy,
                name: sortering.caption,
                labelIcon: (
                  <EDIconButton
                    icon={
                      sortering.order === 'asc'
                        ? 'sort_descending'
                        : 'sort_ascending'
                    }
                    onClick={() => {
                      const newProperties = clone(props.sortProperties);
                      const property = newProperties.find(
                        x => x.orderBy === sortering.orderBy
                      );
                      if (property) {
                        property.order =
                          property.order === 'asc' ? 'desc' : 'asc';
                        updateSortProperties(newProperties);
                      }
                    }}
                    tooltip={
                      sortering.order === 'asc'
                        ? 'Stigande sortering'
                        : 'Fallande sortering'
                    }
                    margin="none"
                    padding="dense"
                  />
                ),
              }))}
              onItemsUpdate={useCallback(
                items => {
                  const newItems = items.map(item => {
                    const sortProp = props.sortProperties.find(
                      prop => item.id === prop.orderBy
                    );
                    return {
                      caption: item.name,
                      orderBy:
                        typeof item.id === 'string'
                          ? item.id
                          : item.id.toString(),
                      order: sortProp?.order ?? 'asc',
                    };
                  });
                  updateSortProperties(newItems);
                },
                [updateSortProperties, props.sortProperties]
              )}
              canDelete
              clickDisabled
            />
          </EDDialogCard>
        </EDGrid>
        <EDGrid item xs={12} sm={6} className={classes.flexContainer}>
          <div className={cx(classes.container, classes.titleHeight)}>
            <EDTypography className={classes.titleText} gutterBottom>
              Sorterbara kolumner
            </EDTypography>
          </div>
          <EDDialogCard padding={0} displayFlex>
            <CheckboxSelectList>
              {props.kolumner.map((x, index) => (
                <EDListItemButton
                  key={index}
                  onClick={() => {
                    const property = props.sortProperties.find(
                      p => p.orderBy === x.attributeDef
                    );
                    const propIndex = property
                      ? props.sortProperties.indexOf(property)
                      : -1;
                    const newProperties = [...props.sortProperties];
                    if (propIndex !== -1) {
                      newProperties.splice(propIndex, 1);
                    } else {
                      newProperties.push({
                        caption: x.caption || '',
                        orderBy: x.attributeDef,
                        order: 'asc',
                      });
                    }
                    props.updateSortProperties(newProperties);
                  }}
                  classes={{ root: classes.listItem }}
                  disableGutters
                  disableRipple
                >
                  <CheckboxSelectListItemView
                    text={x.caption}
                    checked={props.sortProperties.some(
                      s => s.orderBy === x.attributeDef
                    )}
                  />
                </EDListItemButton>
              ))}
            </CheckboxSelectList>
          </EDDialogCard>
        </EDGrid>
      </EDGrid>
    </EDViewportWidthContainer>
  );
};

export default SorteringsValjare;
